import React, { useState, useEffect, useRef } from 'react';
import FashdeckPlanWatermark from './FashdeckPlanWatermark';
import { 
  X, 
  Download, 
  RotateCw, 
  Maximize2, 
  ChevronLeft, 
  ChevronRight,
  MessageCircle,
  Copy,
  Check
} from 'lucide-react';

const EnhancedImagePreview = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  title = 'Image Preview',
  images = [], // Array of images for navigation
  currentIndex = 0,
  onImageChange,
  showMetadata = true,
  allowDownload = true,
  allowShare = true,
  allowEdit = false,
  onDownload,
  onShare,
  onEdit,
  showFashdeckWatermark = false
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [copied, setCopied] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  // Reset state when image changes
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
    setIsImageLoading(true);
    setImageError(false);
    
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Set a timeout to prevent infinite loading (15 seconds)
    loadingTimeoutRef.current = setTimeout(() => {
      console.warn('Image loading timeout - forcing loading state to false');
      setIsImageLoading(false);
      setImageError(true);
    }, 15000);
    
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [imageUrl, currentIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (images.length > 1) navigateImage(-1);
          break;
        case 'ArrowRight':
          if (images.length > 1) navigateImage(1);
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case 'r':
          e.preventDefault();
          handleRotate();
          break;
        case '0':
          e.preventDefault();
          resetView();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, images.length]);

  const navigateImage = (direction) => {
    if (!images.length || !onImageChange) return;
    
    const newIndex = (currentIndex + direction + images.length) % images.length;
    onImageChange(newIndex);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - pan.x,
        y: e.clientY - pan.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.1, Math.min(5, prev * delta)));
  };

  const copyImageUrl = async () => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadImage = () => {
    if (onDownload) {
      onDownload(imageUrl, title);
    } else {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const shareImage = async () => {
    if (onShare) {
      onShare(imageUrl, title);
    } else if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `Check out this image: ${title}`,
          url: imageUrl
        });
        // Share was successful - no need to show any message
        console.log('Share successful');
      } catch (error) {
        // Handle share cancellation or other errors silently
        if (error.name === 'AbortError') {
          // User cancelled the share dialog - this is normal behavior
          console.log('Share cancelled by user');
        } else {
          // Other error occurred - fallback to copy URL
          console.error('Share failed:', error);
          await copyImageUrl();
        }
      }
    } else {
      copyImageUrl();
    }
  };

  const getImageStyle = () => {
    return {
      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
      cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
      transition: isDragging ? 'none' : 'transform 0.3s ease'
    };
  };

  const getImageMetadata = () => {
    if (!imageRef.current) return null;
    
    const img = imageRef.current;
    return {
      dimensions: `${img.naturalWidth} × ${img.naturalHeight}`,
      aspectRatio: (img.naturalWidth / img.naturalHeight).toFixed(2),
      size: 'Unknown', // Would need to fetch from server
      format: imageUrl.split('.').pop().toUpperCase(),
      url: imageUrl
    };
  };

  if (!isOpen || !imageUrl) return null;

  const metadata = getImageMetadata();

  return (
    <div 
      className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[9999] flex items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="relative w-full h-full flex flex-col"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        {/* Floating Controls - Mobile Optimized */}
        <div className="absolute top-4 right-4 z-20 flex flex-col space-y-2">
          {/* Close Button - Always visible */}
          <button
            onClick={onClose}
            className="p-3 bg-black/60 hover:bg-black/80 text-white rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
            title="Close (Esc)"
          >
            <X size={20} />
          </button>
          
          {/* Image Counter - Only show if multiple images */}
          {images.length > 1 && (
            <div className="px-3 py-2 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm font-semibold shadow-lg">
              {currentIndex + 1} / {images.length}
            </div>
          )}
        </div>

        {/* Floating Action Buttons - Bottom Right */}
        <div className="absolute bottom-4 right-4 z-20 flex flex-col space-y-2">
          {/* Main Actions */}
          <div className="flex flex-col space-y-2">
            {allowDownload && (
              <button
                onClick={downloadImage}
                className="p-3 bg-blue-600/80 hover:bg-blue-600 text-white rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
                title="Download"
              >
                <Download size={20} />
              </button>
            )}
            
            {allowShare && (
              <button
                onClick={shareImage}
                className="p-3 bg-green-600/80 hover:bg-green-600 text-white rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
                title="Share on WhatsApp"
              >
                <MessageCircle size={20} />
              </button>
            )}
            
            <button
              onClick={copyImageUrl}
              className="p-3 bg-purple-600/80 hover:bg-purple-600 text-white rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
              title="Copy URL"
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>
          </div>
        </div>

        {/* Minimal Floating Controls - Bottom Left */}
        <div className="absolute bottom-4 left-4 z-20 flex flex-col space-y-2">
          {/* Essential Controls Only */}
          <div className="flex flex-col space-y-2">
            <button
              onClick={handleRotate}
              className="p-3 bg-black/60 hover:bg-black/80 text-white rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
              title="Rotate (R)"
            >
              <RotateCw size={20} />
            </button>
            
            <button
              onClick={resetView}
              className="p-3 bg-black/60 hover:bg-black/80 text-white rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
              title="Reset View (0)"
            >
              <Maximize2 size={20} />
            </button>
          </div>
        </div>

        {/* Zoom Level Indicator - Top Left */}
        <div className="absolute top-4 left-4 z-20 px-3 py-2 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm font-semibold shadow-lg">
          {Math.round(zoom * 100)}%
        </div>

        {/* Navigation Arrows - Mobile Optimized */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => navigateImage(-1)}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-14 h-14 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg touch-manipulation"
              title="Previous Image (←)"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => navigateImage(1)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-14 h-14 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg touch-manipulation"
              title="Next Image (→)"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Main Image Container */}
        <div 
          ref={containerRef}
          className="flex-1 flex items-center justify-center overflow-hidden"
          onMouseDown={handleMouseDown}
        >
          <div className="relative">
            <img
              ref={imageRef}
              src={imageUrl}
              alt={title}
              className="max-w-full max-h-full object-contain select-none"
              style={getImageStyle()}
              draggable={false}
              onLoad={() => {
                // Clear loading timeout since image loaded successfully
                if (loadingTimeoutRef.current) {
                  clearTimeout(loadingTimeoutRef.current);
                }
                setIsImageLoading(false);
                setImageError(false);
                
                // Reset zoom to fit image on load
                if (zoom === 1) {
                  const container = containerRef.current;
                  const img = imageRef.current;
                  if (container && img) {
                    const containerRect = container.getBoundingClientRect();
                    const imgRect = img.getBoundingClientRect();
                    const scaleX = containerRect.width / imgRect.width;
                    const scaleY = containerRect.height / imgRect.height;
                    setZoom(Math.min(scaleX, scaleY, 1));
                  }
                }
              }}
              onError={(e) => {
                // Clear loading timeout
                if (loadingTimeoutRef.current) {
                  clearTimeout(loadingTimeoutRef.current);
                }
                setIsImageLoading(false);
                setImageError(true);
                console.error('Failed to load image:', imageUrl);
                // Set a placeholder or handle error gracefully
                e.target.style.display = 'none';
              }}
            />
            <FashdeckPlanWatermark show={showFashdeckWatermark} />
          </div>
        </div>


        {/* Thumbnail Navigation - Mobile Optimized */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
            <div className="flex space-x-1 bg-black/60 backdrop-blur-sm rounded-xl p-2 max-w-[90vw] overflow-x-auto">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => onImageChange && onImageChange(index)}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 touch-manipulation ${
                    index === currentIndex 
                      ? 'border-white scale-110 shadow-lg' 
                      : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  <img
                    src={img.url || img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isImageLoading && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-30">
            <div className="text-white text-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p>Loading image...</p>
            </div>
          </div>
        )}
        
        {/* Error State */}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-30">
            <div className="text-white text-center p-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X size={32} className="text-red-400" />
              </div>
              <p className="text-lg font-semibold mb-2">Failed to Load Image</p>
              <p className="text-sm text-gray-300 mb-4">The image could not be loaded or timed out</p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => {
                    setImageError(false);
                    setIsImageLoading(true);
                    // Force image reload by modifying the src
                    if (imageRef.current) {
                      const currentSrc = imageRef.current.src;
                      imageRef.current.src = '';
                      setTimeout(() => {
                        if (imageRef.current) {
                          imageRef.current.src = currentSrc;
                        }
                      }, 50);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200"
                >
                  Retry
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedImagePreview;
